import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import {
	json,
	redirect,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react'
import * as cookie from 'cookie'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { CheckboxField, ErrorList, Field } from '#app/components/forms.tsx'
import { Logo } from '#app/components/logo.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import {
	login,
	loginWithEmail,
	requireAnonymous,
} from '#app/utils/auth.server.ts'
import {
	activeProviderNames,
	ProviderConnectionForm,
} from '#app/utils/connections.tsx'
import { enroll_testId_cookieName } from '#app/utils/enroll-test.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { userHasRoleById } from '#app/utils/permissions.server.ts'
import { PasswordSchema, EmailSchema } from '#app/utils/user-validation.ts'
import { handleNewSession } from './login.server.ts'

const LoginFormSchema = z.object({
	email: EmailSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)

	const url = new URL(request.url)
	const searchParams = url.searchParams

	const action = searchParams.get('action')
	const redirectTo = searchParams.get('redirectTo')
	const headers = new Headers()

	// Delete the "enroll-testid" cookie
	headers.append(
		'set-cookie',
		cookie.serialize(`${enroll_testId_cookieName}`, '', {
			path: '/',
			maxAge: -1, // Ensures the cookie is immediately expired
			httpOnly: true,
			secure: true,
		}),
	)

	const redirectUrl = redirectTo
		? `../signup?redirectTo=${redirectTo}`
		: `../signup`

	if (action === 'signup') {
		return redirect(`${redirectUrl}`, {
			headers,
		})
	}
	return json({})
}

export async function action({ request }: ActionFunctionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const session = await loginWithEmail(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Invalid email or password',
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return json(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { session, remember, redirectTo } = submission.value

	const isStudentUser = await userHasRoleById(session.userId, 'student')

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo: isStudentUser
			? '/student/dashboard'
			: redirectTo
				? redirectTo
				: '/app/dashboard',
	})
}

export default function LoginPage() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="flex min-h-full flex-col justify-center pb-32 pt-20">
			<div className="mx-auto w-full max-w-md">
				<div className="mt-8 flex justify-center">
					<Logo className="w-48 sm:w-64" />
				</div>
				<div className="flex flex-col gap-3 text-center">
					<h1 className="text-h2">Log in With Google!</h1>
					<p className="text-body-md text-muted-foreground">
						You can login with Google.
					</p>
				</div>
				<Spacer size="4xs" />

				<div>
					<div className="mx-auto w-full max-w-md px-8">
						<ul className="mb-8 mt-2 flex flex-col gap-5 py-3">
							{activeProviderNames.map((providerName) => (
								<li key={providerName}>
									<ProviderConnectionForm
										type="Login"
										providerName={providerName}
										redirectTo={redirectTo}
									/>
								</li>
							))}
						</ul>

						<div className="flex items-center justify-center gap-2 pt-6">
							<span className="text-muted-foreground">New here?</span>
							<Link
								to={
									redirectTo
										? `.?action=signup&redirectTo=${encodeURIComponent(redirectTo)}`
										: '.?action=signup'
								}
							>
								Create an account
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Login to Marky.in' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
